import React from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getUser } from 'Selectors';
import useDeployedGlobalRules from 'Hooks/query-hooks/global-rules/useDeployedGlobalRules';
import useGlobalRules from 'Hooks/query-hooks/global-rules/useGlobalRules';
import { useGetTenantGroupOverridesInherited } from 'Hooks/query-hooks/tenant-groups/useGetTenantGroupOverrides';
import useGetLandlordInfo from 'Hooks/query-hooks/tenant/useGetLandlordInfo';
import useGetReduxUsers from 'Hooks/reduxHelperHooks/useGetReduxUsers';
import useGetParameters from 'Hooks/query-hooks/parameters/useGetParameters';
import useGetListNames from 'Hooks/query-hooks/list/useGetListNames';
import Layout from 'Components/Layout';
import errorController from 'Components/ErrorScreens';
import RouteLoading from 'Components/RouteLoading';
import Header from './components/Header';
import DeployedGlobalRulesTable from './components/DeployedGlobalRulesTable';
import DeployedGlobalRulesOverview from './components/DeployedGlobalRulesOverview';

const DeployedGlobalRulesPage = () => {
    const location = useLocation();
    const isLandlordDeployment = location.pathname.indexOf('/deployed/landlord/') > -1;
    const allowOverride =
        location.pathname.indexOf('/deployed/landlord/overrides') > -1 ||
        location.pathname.indexOf('/deployed/landlord/override') > -1;
    const { data: landlordInfo } = useGetLandlordInfo();
    const hasLandlord = !!landlordInfo?.hasLandlord;
    const { users } = useGetReduxUsers();
    const parametersAPI = useGetParameters({
        queryParams: {
            status: JSON.stringify(['active'])
        }
    });
    const listNamesAPI = useGetListNames();
    const { landlordId, tenantKey } = useSelector(getUser);
    const isImpersonationMode = landlordId && landlordId !== tenantKey;
    const globalRulesAPI = useGlobalRules({});
    const globalRulesKeyed = _.keyBy(globalRulesAPI.data?.rules, 'id');

    /** @note This is for the Tenant View Deployed page */
    const deployedGlobalRulesAPI = useDeployedGlobalRules({
        enabled:
            !isLandlordDeployment &&
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching,
        listNameMap: listNamesAPI.data,
        parameterData: parametersAPI.data
    });

    /** @note This is for the Landlord View Network Deploy page */
    // inherited landlord rules
    const tenantGroupOverridesInheritedAPI = useGetTenantGroupOverridesInherited({
        users,
        queryEnabled:
            hasLandlord &&
            !listNamesAPI.isLoading &&
            !listNamesAPI.isFetching &&
            !parametersAPI.isLoading &&
            !parametersAPI.isFetching,
        listNameMap: listNamesAPI.data,
        parameterData: parametersAPI.data
    });

    const { hasError: hasGlobalError, errorCmp: errorGlobalCmp } = errorController([globalRulesAPI.error]);
    const { hasError: hasDeployedError, errorCmp: errorDeployedCmp } = errorController([deployedGlobalRulesAPI.error]);

    /** @note Error for bothe pages */
    if (hasGlobalError) return errorGlobalCmp;
    /** @note Error only on the Landlord View Network Deploy page */
    if (hasDeployedError && !isLandlordDeployment) return errorDeployedCmp;

    if (
        globalRulesAPI.isFetching ||
        globalRulesAPI.isLoading ||
        (isLandlordDeployment &&
            hasLandlord &&
            (tenantGroupOverridesInheritedAPI.isLoading || tenantGroupOverridesInheritedAPI.isFetching)) ||
        (!isLandlordDeployment && (deployedGlobalRulesAPI.isLoading || deployedGlobalRulesAPI.isFetching))
    ) {
        return <RouteLoading />;
    }

    // have to do it this way so typescript doesn't yell at us
    if (
        globalRulesAPI.data === undefined ||
        !globalRulesAPI.data ||
        [globalRulesAPI.data].some(_.isEmpty) ||
        (!isLandlordDeployment &&
            (deployedGlobalRulesAPI.data === undefined ||
                !deployedGlobalRulesAPI.data ||
                [deployedGlobalRulesAPI.data].some(_.isEmpty))) ||
        (isLandlordDeployment &&
            (!tenantGroupOverridesInheritedAPI.data?.rules || tenantGroupOverridesInheritedAPI.data === undefined))
    )
        return (
            <Layout>
                <span>There is no rule</span>
            </Layout>
        );

    const landlordFilteredData = tenantGroupOverridesInheritedAPI.data;
    if (allowOverride && landlordFilteredData && landlordFilteredData.rules) {
        landlordFilteredData.rules = landlordFilteredData?.rules.filter((rule) => {
            if (!isImpersonationMode) {
                return rule.allowOverrides && !(rule.id in globalRulesKeyed) && !rule.hiddenFromTenant;
            }
            return rule.allowOverrides && !(rule.id in globalRulesKeyed);
        });
    }

    if (!isImpersonationMode && landlordFilteredData && landlordFilteredData.rules) {
        landlordFilteredData.rules = landlordFilteredData?.rules.filter((rule) => {
            return !rule.hiddenFromTenant;
        });
    }

    return (
        <Layout>
            <Header isLandlordDeployment={isLandlordDeployment} allowOverride={allowOverride} />
            {!allowOverride ? (
                <DeployedGlobalRulesOverview
                    // eslint-disable-next-line max-len
                    data={
                        isLandlordDeployment
                            ? (landlordFilteredData as GlobalRulesData)
                            : (deployedGlobalRulesAPI.data as GlobalRulesData)
                    } // TODO: do this better later
                    isLandlordDeployment={isLandlordDeployment}
                />
            ) : null}
            <DeployedGlobalRulesTable
                rulesData={isLandlordDeployment ? landlordFilteredData!.rules : deployedGlobalRulesAPI.data!.rules}
                allowOverride={allowOverride}
                isLandlordDeployment={isLandlordDeployment}
                sandboxRules={globalRulesAPI.data!.rules}
            />
        </Layout>
    );
};

export default DeployedGlobalRulesPage;
